import DefaultButton from '@/components/common/default/DefaultButton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkuMarketDataProps } from '@/types/SkuPage';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';
import { MdClose } from 'react-icons/md';
import { Fragment, useState } from 'react';
import { AskMarketValue, BidMarketValue, OrderMarketValue } from '@/types/utilities';
import ProfSellerBadge from '@/components/ProfSeller';
import { toDe } from '@/utilites/sku-operations';

export default function SkuMarketData({
  skuAsks,
  skuBids,
  skuOrders,
  loadingMarketData,
}: SkuMarketDataProps) {
  const { translate } = useTypedTranslation<SkuEnum>('sku');
  const [isOpen, setIsOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<'asks' | 'bids' | 'orders'>('asks');

  return (
    <>
      <DefaultButton
        onClick={() => setIsOpen(true)}
        className="tw-rounded-xl tw-bg-white tw-py-2 tw-font-semibold tw-text-gray-700 tw-underline tw-underline-offset-4 tw-transition tw-duration-300 tw-ease-in-out focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 active:tw-scale-95 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        disabled={loadingMarketData}
      >
        <span>
          {translate(SkuEnum.view_market_data)} (
          {skuAsks.length + skuBids.length + skuOrders.length})
        </span>
      </DefaultButton>
      <Transition appear show={isOpen} as="div">
        <Dialog
          open={isOpen}
          as="div"
          className="tw-relative tw-z-[99999] focus:tw-outline-none max-nine:tw-p-2"
          onClose={() => setIsOpen(false)}
        >
          <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-60" />

          <div className="tw-fixed tw-inset-0 tw-z-10 tw-w-screen tw-overflow-y-auto tw-bg-black tw-bg-opacity-30">
            <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4 max-nine:tw-p-0">
              <TransitionChild
                as={Fragment}
                enter="tw-ease-out tw-duration-300"
                enterFrom="tw-opacity-0 tw-scale-95"
                enterTo="tw-opacity-100 tw-scale-100"
                leave="tw-ease-in tw-duration-200"
                leaveFrom="tw-opacity-100 tw-scale-100"
                leaveTo="tw-opacity-0 tw-scale-95"
              >
                <div className="tw-contents">
                  <DialogPanel
                    as="div"
                    transition
                    className="data-[closed]:tw-transform-[scale(95%)] tw-w-full tw-max-w-2xl tw-rounded-xl tw-bg-white tw-shadow-md tw-backdrop-blur-2xl tw-duration-300 tw-ease-out data-[closed]:tw-opacity-0 max-nine:tw-h-screen max-nine:tw-w-screen max-nine:tw-max-w-[unset] max-nine:tw-overflow-y-scroll"
                  >
                    <DialogTitle
                      as="h3"
                      className="tw-flex tw-items-center tw-justify-between tw-border-l-0 tw-px-6 tw-py-3 tw-text-center tw-text-2xl tw-font-semibold tw-text-textBlack"
                    >
                      {translate(SkuEnum.market_data)}
                      <DefaultButton aria-label="Close" onClick={() => setIsOpen(false)}>
                        <MdClose />
                      </DefaultButton>
                    </DialogTitle>
                    <div className="tw-flex tw-w-full tw-items-center tw-bg-customCardGray tw-p-3 child:tw-basis-full child:tw-text-base child:tw-font-semibold max-nine:tw-border max-nine:tw-border-l-0 max-nine:tw-border-r-0 max-nine:tw-border-t-0 max-nine:tw-border-solid max-nine:tw-border-custom-gray max-nine:tw-bg-white max-nine:tw-px-4 max-nine:tw-pb-4">
                      <DefaultButton
                        className={clsx(
                          'tw-rounded-md tw-py-1 tw-font-plusJakartaSans !tw-text-base tw-font-bold tw-text-textBlack tw-transition-all tw-duration-300',
                          {
                            'hover:tw-text-whitefocus:tw-ring-2 tw-bg-main tw-text-white hover:tw-bg-main focus:tw-ring-blue-500 active:tw-bg-gray-200 active:tw-text-text disabled:tw-bg-gray-300 disabled:tw-text-gray-500':
                              currentTab === 'asks',
                          }
                        )}
                        onClick={() => setCurrentTab('asks')}
                      >
                        {translate(SkuEnum.asks)}
                      </DefaultButton>
                      <DefaultButton
                        className={clsx(
                          'tw-rounded-md tw-py-1 tw-font-plusJakartaSans !tw-text-base tw-font-bold tw-text-textBlack tw-transition-all tw-duration-300',
                          {
                            'tw-bg-main tw-text-white hover:tw-bg-main hover:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 active:tw-bg-gray-200 active:tw-text-text disabled:tw-bg-gray-300 disabled:tw-text-gray-500':
                              currentTab === 'bids',
                          }
                        )}
                        onClick={() => setCurrentTab('bids')}
                      >
                        {translate(SkuEnum.bids)}
                      </DefaultButton>
                      <DefaultButton
                        className={clsx(
                          'tw-rounded-md tw-py-1 tw-font-plusJakartaSans !tw-text-base tw-font-bold tw-text-textBlack tw-transition-all tw-duration-300',
                          {
                            'tw-bg-main tw-text-white hover:tw-bg-main hover:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 active:tw-bg-gray-200 active:tw-text-text disabled:tw-bg-gray-300 disabled:tw-text-gray-500':
                              currentTab === 'orders',
                          }
                        )}
                        onClick={() => setCurrentTab('orders')}
                      >
                        {translate(SkuEnum.orders)}
                      </DefaultButton>
                    </div>
                    <div className="tw-p-3">
                      {['asks', 'bids', 'orders'].map(
                        tab =>
                          currentTab === tab && (
                            <Transition
                              key={tab}
                              appear
                              show={currentTab === tab}
                              enter="tw-transition-opacity tw-duration-500"
                              enterFrom="tw-opacity-0"
                              enterTo="tw-opacity-100"
                              leave="tw-transition-opacity tw-duration-500"
                              leaveFrom="tw-opacity-100"
                              leaveTo="tw-opacity-0"
                              unmount
                            >
                              <div>
                                <h3 className="tw-font-plusJakartaSans tw-text-lg tw-font-semibold tw-text-textBlack nine:tw-hidden">
                                  {currentTab === 'asks'
                                    ? translate(SkuEnum['all_asks'])
                                    : currentTab === 'bids'
                                      ? translate(SkuEnum['all_bids'])
                                      : translate(SkuEnum['all_orders'])}
                                </h3>

                                <table className="tw-w-full tw-border-collapse tw-rounded-md tw-border-b tw-border-custom-gray">
                                  <thead>
                                    <tr className="tw-border-b tw-border-custom-gray">
                                      <th className="tw-px-4 tw-py-2 tw-text-center tw-font-semibold tw-text-textBlack">
                                        {translate(SkuEnum.date)}
                                      </th>
                                      <th className="tw-px-4 tw-py-2 tw-text-center tw-font-semibold tw-text-textBlack">
                                        {currentTab === 'asks'
                                          ? translate(SkuEnum['ask_value'])
                                          : currentTab === 'bids'
                                            ? translate(SkuEnum['bid_value'])
                                            : translate(SkuEnum['order_value'])}
                                      </th>

                                      {tab === 'asks' && (
                                        <th>
                                          <span className="tw-sr-only">
                                            {translate(SkuEnum.professional_seller)}
                                          </span>
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tab === 'asks' && skuAsks.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={4}
                                          className="tw-p-2 tw-text-center tw-text-sm tw-text-textBlack"
                                        >
                                          {translate(SkuEnum.no_asks_available)}
                                        </td>
                                      </tr>
                                    )}
                                    {tab === 'bids' && skuBids.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={3}
                                          className="tw-p-2 tw-text-center tw-text-sm tw-text-textBlack"
                                        >
                                          {translate(SkuEnum.no_bids_available)}
                                        </td>
                                      </tr>
                                    )}
                                    {tab === 'orders' && skuOrders.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={3}
                                          className="tw-p-2 tw-text-center tw-text-sm tw-text-textBlack"
                                        >
                                          {translate(SkuEnum.no_orders_available)}
                                        </td>
                                      </tr>
                                    )}
                                    {(tab === 'asks'
                                      ? skuAsks
                                      : tab === 'bids'
                                        ? skuBids
                                        : skuOrders
                                    ).map((item, index) => {
                                      const bgColor = index % 2 === 0 ? 'tw-bg-[#DDE7FC80]' : '';

                                      if (tab === 'asks') {
                                        const itemWithType = item as AskMarketValue;
                                        return (
                                          <tr key={itemWithType.id} className={bgColor}>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {new Date(itemWithType.created).toLocaleDateString()}
                                            </td>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {toDe(itemWithType.value)}
                                            </td>

                                            <td className="tw-text-center">
                                              {itemWithType.isSellerProfessional && (
                                                <div className="tw-px-2 tw-text-sm">
                                                  <ProfSellerBadge type="ask" />
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      }
                                      if (tab === 'bids') {
                                        const itemWithType = item as BidMarketValue;
                                        return (
                                          <tr key={itemWithType.id} className={bgColor}>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {new Date(itemWithType.created).toLocaleDateString()}
                                            </td>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {toDe(itemWithType.value)}
                                            </td>
                                          </tr>
                                        );
                                      }
                                      if (tab === 'orders') {
                                        const itemWithType = item as OrderMarketValue;
                                        return (
                                          <tr key={index} className={bgColor}>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {new Date(
                                                itemWithType.skuOrderStatus.paid
                                              ).toLocaleDateString()}
                                            </td>
                                            <td className="tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-text-textBlack">
                                              {toDe(itemWithType.payValue)}
                                            </td>
                                          </tr>
                                        );
                                      }
                                      return null;
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </Transition>
                          )
                      )}
                    </div>
                    <div className="tw-mt-4"></div>
                  </DialogPanel>
                </div>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
